/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, ember/require-tagless-components, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember Imports
import { service } from '@ember/service';
import { get, set, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import config from 'linced-app/config/environment';
// Internal Imports
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import EventStatus from '@linc-technologies/linced-common/enums/events/event-status';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import {
  byKeys,
  filterBy,
  findBy,
  mapBy,
  rejectBy,
  resolved,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class PermissionReplies extends Component {
  // Services
  @service
  fetch;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  EventStatus = EventStatus;
  Scopes = Scopes;
  event = null;
  @tracked permissionReplies = [];
  @tracked
  select = '';
  @tracked
  selectedLearners = [];
  @tracked
  optionFilters = [];
  @tracked
  permissionFilters = [];
  @tracked optionSortDir = 'asc';
  @tracked optionSortField = 'person.firstName';
  @tracked permissionSortDir = 'asc';
  @tracked permissionSortField = 'person.firstName';
  @tracked
  dialogOpen = false;
  @tracked
  loading = false;
  @tracked
  accountMap = null;
  @tracked
  lineItems = [];

  @computed('groupMembers.[]', 'selectedLearners.[]')
  get selectedGroupMembers() {
    let selectedLearnerIds = mapBy(this.selectedLearners, 'id');
    return this.groupMembers.filter((groupMember) => {
      return selectedLearnerIds.includes(groupMember.personId);
    });
  }

  @computed(
    'event.groups.content.[]',
    'permissionReplies.[]',
    'accountMap.[]',
    'lineItems.[]',
  )
  get groupMembers() {
    // resolve the async hasMany relationship upfront.
    let groupMembers = resolved(this.event.groups).reduce((acc, group) => {
      acc.push(...group.currentLearners);
      return acc;
    }, []);
    // Reset these each time because they stick
    groupMembers.forEach((member) => set(member, 'permissionReply', null));
    groupMembers.forEach((member) => set(member, 'history', false));
    // sortBy gives us a cheap nasty way to ensure we attach the most recent reply
    sortBy(this.permissionReplies, 'createTime').forEach((reply) => {
      let groupMember = findBy(groupMembers, 'personId', reply.person_id);
      if (groupMember) {
        // Cheap way to show if this member has more than one reply
        if (groupMember.permissionReply) {
          groupMember.set('history', true);
        }
        groupMember.set('permissionReply', reply);
      }
    });

    if (this.accountMap && this.lineItems) {
      groupMembers.forEach((member) => {
        let accountId = this.accountMap[member.person.id];
        if (!accountId) {
          return;
        }

        member.set('paid', {});
        let memberLineItems = filterBy(this.lineItems, 'account_id', accountId);
        // Calculate Intention to Attend payment status.
        // The option value can be '' (Go zero value). A set() path cannot end
        // in an empty key, so fill plain objects and set them one time.
        let intentionToAttendLineItems = rejectBy(memberLineItems, 'optionId');
        let intentionToAttend = member.get('permissionReply.intentionToAttend');
        let intentionToAttendOption = {};
        let intentionToAttendPaid = {};
        this.event.intentionToAttendCostMappingOptions.forEach(
          (intentionToAttendCostMappingOption) => {
            let value = intentionToAttendCostMappingOption.value;
            intentionToAttendOption[value] = intentionToAttend ? 'Yes' : 'No';
            let optionLineItems = filterBy(
              intentionToAttendLineItems,
              'optionValue',
              value,
            );
            if (optionLineItems) {
              intentionToAttendPaid[value] = intentionToAttend
                ? this.calculateLineItemDisplay(optionLineItems)
                : '';
            }
          },
        );
        member.set('intentionToAttendOption', intentionToAttendOption);
        member.set('intentionToAttendPaid', intentionToAttendPaid);

        //Calculate Option Payment Status
        if (member.permissionReply) {
          member.permissionReply.fieldsArray.forEach((field) => {
            let optionLineItems = filterBy(
              memberLineItems,
              'optionId',
              field.key.replaceAll(':', '-'),
            );
            let selections = [];
            switch (field.datatype) {
              case DataType.STRING:
                selections.push(field.valueString);
                break;
              case DataType.LIST:
                selections.push(...field.valueList.toArray());
                break;
              case DataType.BOOLEAN:
                if (field.valueBool) {
                  selections.push('true');
                }
                break;
              case DataType.INTEGER:
                if (field.valueInt > 0) {
                  selections.push('true');
                }
                break;
            }
            let selectionLineItems = optionLineItems.filter((item) => {
              return selections.includes(item.optionValue);
            });
            if (selectionLineItems) {
              member.set(
                'paid.' + field.key,
                this.calculateLineItemDisplay(selectionLineItems),
              );
            }
          });
        }
      });
    }

    return groupMembers;
  }

  calculateLineItemDisplay(lineItems) {
    let totalBalance = lineItems.reduce((acc, item) => {
      return acc + item.balance;
    }, 0);
    let totalDisplayAmount = lineItems.reduce((acc, item) => {
      return acc + item.displayAmount;
    }, 0);
    let message = 'Partly';
    let note = `$${totalBalance} of $${totalDisplayAmount} outstanding`;
    if (totalDisplayAmount === 0) {
      message = 'n/a';
      note = '';
    } else if (totalBalance === totalDisplayAmount) {
      message = 'No';
      note = '';
    } else if (totalBalance === 0) {
      message = 'Yes';
      note = '';
    }
    return { message, note };
  }

  @computed('groupMembers.{[],@each.intentionToAttend}')
  get attendingGroupMembers() {
    return filterBy(this.groupMembers, 'intentionToAttend', true);
  }

  @computed('groupMembers.@each.intentionToAttend')
  get notAttendingGroupMembers() {
    return rejectBy(
      filterBy(this.groupMembers, 'permissionReply'),
      'intentionToAttend',
      true,
    );
  }

  @computed('groupMembers.@each.permissionReply')
  get noReplyGroupMembers() {
    return rejectBy(this.groupMembers, 'permissionReply');
  }

  @computed(
    'groupMembers.[]',
    'permissionFilters.@each.{field,value}',
    'permissionSortDir',
    'permissionSortField',
  )
  get sortedFilteredPermissionGroupMembers() {
    let filteredPermissionGroupMembers = [];
    if (isEmpty(this.permissionFilters)) {
      // No filters - just return the data.
      filteredPermissionGroupMembers = this.groupMembers;
    } else {
      // Determine datatypes for any specified filters.
      let filters = this.permissionFilters.map((filter) => {
        return {
          key: filter.field,
          dataType: DataType.STRING,
          value: filter.value,
        };
      });

      // Apply all filters to each row in one hit, to avoid iterating over a
      // (potentially) large dataset multiple times.
      filteredPermissionGroupMembers = this.groupMembers.filter((row) => {
        return filters.every((filter) => {
          // Generic filter processing.
          return filterValue(
            get(row, filter.key) ?? '',
            filter.dataType,
            filter.value,
          );
        });
      });
    }

    let sortedFilteredPermissionGroupMembers =
      filteredPermissionGroupMembers.sort(byKeys(this.permissionSortField));
    if (this.permissionSortDir === 'desc') {
      return sortedFilteredPermissionGroupMembers.reverse();
    }
    return sortedFilteredPermissionGroupMembers;
  }

  @computed(
    'groupMembers.[]',
    'optionFilters.@each.{field,value}',
    'optionSortDir',
    'optionSortField',
  )
  get sortedFilteredOptionGroupMembers() {
    let filteredOptionGroupMembers = [];
    if (isEmpty(this.optionFilters)) {
      // No filters - just return the data.
      filteredOptionGroupMembers = this.groupMembers;
    } else {
      // Determine datatypes for any specified filters.
      let filters = this.optionFilters.map((filter) => {
        return {
          key: filter.field,
          dataType: DataType.STRING,
          value: filter.value,
        };
      });

      // Apply all filters to each row in one hit, to avoid iterating over a
      // (potentially) large dataset multiple times.
      filteredOptionGroupMembers = this.groupMembers.filter((row) => {
        return filters.every((filter) => {
          // Generic filter processing.
          return filterValue(
            get(row, filter.key) ?? '',
            filter.dataType,
            filter.value,
          );
        });
      });
    }

    let sortedFilteredOptionGroupMembers = filteredOptionGroupMembers.sort(
      byKeys(this.optionSortField),
    );
    if (this.optionSortDir === 'desc') {
      return sortedFilteredOptionGroupMembers.reverse();
    }
    return sortedFilteredOptionGroupMembers;
  }

  @computed(
    'selectedLearners.[]',
    'groupMembers.@each.{permissionReply,intentionToAttend,person}',
  )
  get selectedHaventReplied() {
    let members = this.groupMembers || [];

    let repliedIds = [];
    filterBy(members, 'intentionToAttend', true).forEach((member) => {
      repliedIds.push(member.person.id);
    });

    rejectBy(
      filterBy(members, 'permissionReply'),
      'intentionToAttend',
      true,
    ).forEach((member) => {
      repliedIds.push(member.person.id);
    });

    let selectedIds = mapBy(this.selectedLearners, 'id');

    let respondents = selectedIds.filter((item) =>
      new Set(repliedIds).has(item),
    );

    return respondents.length === 0;
  }

  getHighestPriorityPrimaryContactWithAccess(learner) {
    return (
      learner.learner.primaryContacts.find((contact) => {
        return contact.allowedAccess;
      }) || null
    );
  }

  @action
  refresh() {
    let updates = [];
    updates.push(
      this.store
        .query('events/permission-reply', {
          schoolId: this.event.school_id,
          eventId: this.event.id,
        })
        .then((permissionReplies) => {
          this.permissionReplies = permissionReplies;
          this.store
            .query('finance/account', {
              personIds: JSON.stringify(mapBy(permissionReplies, 'person_id')),
            })
            .then((accounts) => {
              let accountMap = {};
              accounts.forEach((account) => {
                accountMap[account.person_id] = account.id;
              });
              this.accountMap = accountMap;
            });
        }),
    );
    updates.push(
      this.store
        .query('finance/line-item', {
          schoolId: this.event.school_id,
          eventId: this.event.id,
        })
        .then((lineItems) => {
          this.lineItems = rejectBy(lineItems, 'isArchived');
        }),
    );
    return Promise.all(updates);
  }

  @action
  sendReminderNotification() {
    this.selectedLearners.forEach((learner) => {
      let primaryContact =
        this.getHighestPriorityPrimaryContactWithAccess(learner);

      if (!primaryContact) {
        this.toaster.error(
          `Error sending reminder notification for ${learner.fullName}`,
        );
        return;
      }

      let url = `${config.APP.apiHost}/notifications/${config.APP.apiPath}/events/reminder`;
      let data = {
        schoolId: this.session.school.id,
        title: this.event.title,
        location: this.event.location,
        time: this.event.start,
        eventId: this.event.id,
        personId: primaryContact.caregiverId,
      };
      let options = {
        data: JSON.stringify(data),
      };
      this.fetch
        .post(url, options)
        .then(() => {
          this.selectedLearners = [];
          this.toaster.success(
            `Successfully sent reminder notification to ${learner.fullName}'s caregiver`,
          );
        })
        .catch(() => {
          this.toaster.error('Error sending reminder notification');
        });
    });
  }

  @action
  handleSelect(select) {
    if (select === this.select) {
      select = '';
    }
    this.select = select;
    this.selectedLearners = [];
    let selectedLearners = [];
    switch (select) {
      case 'attending':
        selectedLearners = filterBy(
          this.groupMembers,
          'intentionToAttend',
          true,
        );
        break;
      case 'pending':
        selectedLearners = filterBy(
          this.groupMembers,
          'permissionReply.pending',
        );
        break;
      case 'not-attending':
        selectedLearners = rejectBy(
          filterBy(this.groupMembers, 'permissionReply'),
          'intentionToAttend',
          true,
        );
        break;
      case 'no-replies':
        selectedLearners = rejectBy(this.groupMembers, 'permissionReply');
    }
    this.selectedLearners = mapBy(selectedLearners, 'person');
  }

  @action
  openDialog() {
    this.loading = true;
    this.refresh().then(() => {
      this.optionFilters = [];
      this.permissionFilters = [];
      this.optionSortDir = 'asc';
      this.optionSortField = 'person.firstName';
      this.permissionSortDir = 'asc';
      this.permissionSortField = 'person.firstName';
      this.select = '';
      this.selectedLearners = [];
      this.loading = false;
      this.dialogOpen = true;
    });
  }

  @action
  filter(propertyName, field, value) {
    let filters = this[propertyName];
    let filter = findBy(filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this[propertyName] = [...filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this[propertyName] = filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  toggleLearner(learner, checked) {
    this.select = '';
    if (checked) {
      this.selectedLearners = [...this.selectedLearners, learner];
    } else {
      this.selectedLearners = this.selectedLearners.filter(
        (l) => l !== learner,
      );
    }
  }

  @action
  toggleLearners(learners, checked) {
    this.select = '';
    let selectedLearners = this.selectedLearners;
    learners.forEach((learner) => {
      if (checked) {
        if (!selectedLearners.includes(learner)) {
          selectedLearners = [...selectedLearners, learner];
        }
      } else {
        selectedLearners = selectedLearners.filter((l) => l !== learner);
      }
    });
    this.selectedLearners = selectedLearners;
  }
}
